import { Organization } from './organization.model';
import { Project } from './project.model';
import { Task } from './task.model';
import { User } from './user.model';
import { Document } from './document.model';

export enum BudgetRequestStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

export enum BudgetRequestRecipientStatus {
  SENT = 'SENT',
  VIEWED = 'VIEWED',
  RESPONDED = 'RESPONDED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum BudgetProposalStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  COUNTERED = 'COUNTERED',
}

export interface BudgetRequestRecipient {
  budgetRequestId: string;
  recipientOrganizationId: string;
  status: BudgetRequestRecipientStatus;
  respondedAt?: string | Date;
  recipientOrganization?: Organization;
}

export interface BudgetProposal {
  budgetProposalId: string;
  budgetRequestId: string;
  proposingOrganizationId: string;
  description?: string;
  proposedStartDate: string | Date;
  proposedEndDate: string | Date;
  proposedAmount: number;
  status: BudgetProposalStatus;
  isCounterProposal: boolean;
  parentProposalId?: string;
  createdBy: string;
  createdAt: string | Date;
  updatedAt?: string | Date;
  proposingOrganization?: Organization;
  createdByUser?: User;
  budgetRequest?: BudgetRequest;
  documents?: Document[];
}

export interface BudgetRequest {
  budgetRequestId: string;
  /** Derived by the API on list queries. Absent on single-record fetches. */
  rowStatus?: BudgetRowStatus;
  taskId: string;
  description?: string;
  requestedStartDate: string | Date;
  requestedEndDate: string | Date;
  maxBudgetAmount?: number;
  status: BudgetRequestStatus;
  originatorOrganizationId: string;
  createdBy: string;
  createdAt: string | Date;
  updatedAt?: string | Date;
  delegateTaskId?: string; // NEW: ID of the task created from this budget
  delegationCancelledAt?: string | Date; // NEW: When delegation was cancelled
  task?: Task & {
    project?: Project;
  };
  originatorOrganization?: Organization;
  createdByUser?: User;
  recipients?: BudgetRequestRecipient[];
  proposals?: BudgetProposal[];
  documents?: Document[];
}

// Input types for mutations
export interface BudgetRequestCreateInput {
  taskId: string;
  description?: string;
  requestedStartDate: string;
  requestedEndDate: string;
  maxBudgetAmount?: number;
  recipientOrganizationIds: string[];
  files?: File[];
}

export interface BudgetProposalCreateInput {
  budgetRequestId: string;
  description?: string;
  proposedStartDate: string;
  proposedEndDate: string;
  proposedAmount: number;
  files?: File[];
}

export interface BudgetCounterProposalCreateInput {
  parentProposalId: string;
  description?: string;
  proposedStartDate: string;
  proposedEndDate: string;
  proposedAmount: number;
  files?: File[];
}

// Request/Response types
export interface GetBudgetRequestsResponse {
  budgetRequestsByOrganization: BudgetRequest[];
}

export interface GetPendingBudgetRequestsResponse {
  pendingBudgetRequests: BudgetRequest[];
}

export interface GetBudgetRequestDetailsResponse {
  budgetRequestDetails: BudgetRequest;
}

export interface GetBudgetProposalConversationResponse {
  budgetProposalConversation: BudgetProposal[];
}

export interface CreateBudgetRequestResponse {
  createBudgetRequest: BudgetRequest;
}

export interface RespondToBudgetRequestResponse {
  respondToBudgetRequest: BudgetProposal;
}

export interface CreateCounterProposalResponse {
  createCounterProposal: BudgetProposal;
}

export interface AcceptBudgetProposalResponse {
  acceptBudgetProposal: BudgetProposal;
}

export interface RejectBudgetProposalResponse {
  rejectBudgetProposal: BudgetProposal;
}

export interface CancelBudgetRequestResponse {
  cancelBudgetRequest: BudgetRequest;
}

// Pagination types
export type BudgetPerspective = 'SENT' | 'RECEIVED';
export type BudgetStatusFilter = 'ACTIVE' | 'COMPLETED' | 'ALL';

/**
 * The status a bid shows in a list, from this organization's point of view.
 * Derived by the API — never computed here, so the label we filter by and the
 * label we render can never disagree.
 */
export type BudgetRowStatus =
  | 'NEW'
  | 'COUNTERED'
  | 'NEGOTIATING'
  | 'RESPONDED'
  | 'AWAITING'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'CLOSED';

/** Order the status filters appear in, roughly most-urgent first. */
export const BUDGET_ROW_STATUS_ORDER: BudgetRowStatus[] = [
  'NEW',
  'COUNTERED',
  'NEGOTIATING',
  'RESPONDED',
  'AWAITING',
  'VIEWED',
  'ACCEPTED',
  'REJECTED',
  'CLOSED',
  'CANCELLED',
];

/** Human-facing label for each status. Keep these short — they render inside a pill. */
export const BUDGET_ROW_STATUS_LABEL: Record<BudgetRowStatus, string> = {
  NEW: 'New',
  COUNTERED: 'Countered',
  NEGOTIATING: 'Negotiating',
  RESPONDED: 'Responded',
  AWAITING: 'Awaiting reply',
  VIEWED: 'Viewed',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  CLOSED: 'Closed',
};

export interface BudgetStatusFacet {
  status: BudgetRowStatus;
  count: number;
}

export interface GetPaginatedBudgetRequestsInput {
  page: number;
  limit: number;
  perspective?: BudgetPerspective;
  statusFilter?: BudgetStatusFilter;
  /** Precise status filter. Omitted when empty — an empty list is not "match nothing". */
  statuses?: BudgetRowStatus[];
  /** Free-text match against task and project names. */
  search?: string;
}

export interface PaginatedBudgetRequestsResponse {
  budgetRequests: BudgetRequest[];
  count: number;
  /** Counts per status across the whole result set, before the status filter narrows it. */
  facets: BudgetStatusFacet[];
  totalBeforeStatusFilter: number;
  actionableCount: number;
}
